import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { ApiError, api } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { salesApi } from "../../lib/sales-api";
import {
  ANSWERED_OUTCOMES,
  ATTEMPT_LABELS,
  type SalesAttempt,
  type SalesAttemptOutcome,
  type SalesCallDetail,
} from "../../lib/sales-types";
import { absoluteTime, relativeTime, shortDateTime } from "../../lib/time";
import type { User } from "../../types";
import { AttentionBadge } from "../../components/sales/AttentionBadge";
import { SlaIndicator } from "../../components/SlaClock";
import { salesSla } from "../../lib/sales-sla";
import { toneClass } from "../../components/sales/CallRow";
import { Chip } from "../../components/sales/NewCallDialog";
import { NewMeetingDialog } from "../../components/meetings/NewMeetingDialog";
import { NewTicketDialog } from "../../components/NewTicketDialog";
import { Avatar } from "../../components/ui/Avatar";
import { Button } from "../../components/ui/Button";
import { Overlay } from "../../components/ui/Overlay";
import { Select } from "../../components/ui/Select";
import { cn } from "../../components/ui/cn";

/**
 * One call: everything known about it, everything tried, and the two things
 * you can do next.
 *
 * Laid out as a working screen rather than a record. The logging form is at the
 * top, above the history, because this page is open *while the phone is
 * ringing* -- a form below a timeline of eight attempts is a form you scroll to
 * find with a handset in your other hand. The history is underneath, where it
 * is read afterwards.
 *
 * Closing is a second, separate form and is deliberately not one click. A score
 * is the outcome of the whole pursuit and it lands in the customer's CRM
 * history; making it as easy as logging a no-answer is how calls get scored by
 * accident.
 */
export function CallPage({
  user,
  onClose,
}: {
  user: User;
  /** Dismisses the dialog. See the note above on why this is one. */
  onClose: () => void;
}) {
  const { callId } = useParams();
  const id = Number(callId);
  const client = useQueryClient();
  const [logging, setLogging] = useState(false);
  const [booking, setBooking] = useState(false);
  /* The follow-up email's new-ticket dialog, opened by "Close + email" once
     the close it follows is in. */
  const [emailing, setEmailing] = useState(false);
  /* Whether the close form is showing. Lifted out of the form so the button
     beside "Log an attempt" can open it -- see the note there. */
  const [closing, setClosing] = useState(false);

  const call = useQuery({
    queryKey: queryKeys.salesCall(id),
    queryFn: () => salesApi.call(id),
    enabled: Number.isFinite(id),
  });

  const config = useQuery({
    queryKey: queryKeys.salesConfig,
    queryFn: salesApi.config,
    staleTime: 5 * 60_000,
  });

  const users = useQuery({
    queryKey: queryKeys.users,
    queryFn: api.users,
    staleTime: 5 * 60_000,
    enabled: user.role === "admin",
  });

  const refresh = () => {
    void client.invalidateQueries({ queryKey: queryKeys.salesRoot });
  };

  const act = useMutation({
    mutationFn: (action: () => Promise<SalesCallDetail | void>) => action(),
    onSuccess: refresh,
  });

  if (call.isLoading || call.isError || !call.data) {
    return (
      <Overlay onClose={onClose}>
        <p className="p-8 text-center text-sm text-faint">
          {call.isLoading
            ? "Loading…"
            : "That call is not here. It may have been deleted."}
        </p>
      </Overlay>
    );
  }

  const row = call.data;
  const isAdmin = user.role === "admin";
  const mine = row.assigneeUserId === user.id;
  const scores = (config.data?.scores ?? []).filter((score) => score.isActive);

  return (
    <Overlay onClose={onClose}>
      <header className="mb-4">
        <div className="mb-1 flex flex-wrap items-center gap-2 pr-8">
          <h1 className="text-lg font-semibold text-content">
            {row.companyName}
          </h1>

          <AttentionBadge attention={row.attention} />

          {/* The same clock as the list and as a ticket. On the page it earns
              the larger size: this is where somebody decides whether to ring
              now or later. */}
          {row.status === "open" ? (
            <SlaIndicator sla={salesSla(row)} size="md" />
          ) : null}

          {row.pinnedAt ? (
            <span className="rounded-full bg-brand-600 px-2 py-0.5 text-[11px] font-medium text-white">
              Pinned
            </span>
          ) : null}

          {row.status === "closed" ? (
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[11px] font-medium",
                toneClass(row.scoreTone),
              )}
            >
              {row.scoreLabel ?? "Closed"}
            </span>
          ) : null}
        </div>

        {row.subject ? (
          <p className="text-sm text-muted">{row.subject}</p>
        ) : null}

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-faint">
          {row.groupName ? <span>{row.groupName}</span> : null}
          {row.accountManagerName ? (
            <span title="Account manager in the CRM">
              managed by {row.accountManagerName}
            </span>
          ) : null}
          {row.phone ? (
            /* A real tel: link. Half this desk works from a softphone, and
               retyping a number off the screen is how you ring the wrong one. */
            <a href={`tel:${row.phone}`} className="text-brand-600 hover:underline">
              {row.phone}
            </a>
          ) : null}
          {row.contactName ? <span>Ask for {row.contactName}</span> : null}
          {row.companyId ? (
            <Link
              to={`/crm/companies/${row.companyId}`}
              className="text-brand-600 hover:underline"
            >
              Open in CRM
            </Link>
          ) : (
            <span title="Closed calls are only written into a CRM account when one is linked">
              Not linked to a CRM account
            </span>
          )}
          {/* Beside the CRM link, because the orders somebody wants in front
              of them before ringing are Team's. Only when the account is
              linked to Team -- a link to nowhere is worse than none. */}
          {row.teamUrl ? (
            <a
              href={row.teamUrl}
              target="_blank"
              rel="noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open in Team ↗
            </a>
          ) : null}
          {/* The date *and* the time. A call log is read to reconstruct a
              day -- "rang at 09:15, again at 14:30" -- and a relative phrase
              cannot answer that. The relative form stays as the aside, since
              it is still the faster read for "was this recent". */}
          <span title={absoluteTime(row.createdAt)}>
            raised {shortDateTime(row.createdAt)}{" "}
            <span className="text-faint">({relativeTime(row.createdAt)})</span>
          </span>
          <DueDate
            value={row.dueAt}
            disabled={row.status === "closed" || act.isPending}
            onSave={(next) => act.mutate(() => salesApi.update(id, { dueAt: next }))}
          />
        </div>
      </header>

      {act.isError ? (
        <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-500/10 dark:text-red-300">
          {act.error instanceof ApiError
            ? act.error.message
            : "That did not work."}
        </p>
      ) : null}

      {/* ── Who has it ─────────────────────────────────────────────────── */}
      <section className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface p-3">
        {row.assigneeUserId === null ? (
          <>
            <span className="text-sm text-muted">Nobody has claimed this.</span>
            {row.status === "open" ? (
              <Button
                size="sm"
                disabled={act.isPending}
                onClick={() => act.mutate(() => salesApi.claim(id))}
              >
                Claim it
              </Button>
            ) : null}
          </>
        ) : (
          <>
            <Avatar
              agent={{ id: row.assigneeUserId, name: row.assigneeName }}
              size="sm"
            />
            <span className="text-sm text-content">
              {mine ? "Yours" : row.assigneeName}
            </span>
            {row.claimedAt ? (
              <span className="text-xs text-faint" title={absoluteTime(row.claimedAt)}>
                since {shortDateTime(row.claimedAt)}
              </span>
            ) : null}

            {row.status === "open" && (mine || isAdmin) ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={act.isPending}
                onClick={() =>
                  act.mutate(async () => {
                    await salesApi.release(id);
                    /* Back to the list. Letting a call go is a decision *not*
                       to work it, and leaving somebody staring at the call
                       they just put down -- with a Claim button where the
                       release button was -- invites taking it straight back.
                       Closed rather than navigated, so it is the list they
                       came from, still where they left it. */
                    onClose();
                  })
                }
              >
                Put back in the pool
              </Button>
            ) : null}
          </>
        )}

        {/* A line of its own on a phone, the select taking the width.
            Pushed right with `ml-auto` it wrapped there anyway, and sat under
            "Claim it" as a ragged second step at whatever width "Unassigned"
            happened to give it. */}
        {isAdmin && row.status === "open" ? (
          <span className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto">
            <Select
              className="flex-1 sm:flex-none"
              value={row.assigneeUserId === null ? "" : String(row.assigneeUserId)}
              onChange={(value) =>
                act.mutate(() => salesApi.assign(id, value ? Number(value) : null))
              }
              label="Assign to"
              options={[
                { value: "", label: "Unassigned" },
                ...(users.data ?? []).map((person) => ({
                  value: String(person.id),
                  label: person.name,
                })),
              ]}
            />

            <Button
              size="sm"
              /*
                Pink either way. It was secondary until pinned, which made the
                pair read as two different controls -- and "Work next" was a
                claim about where the call goes, when in fact every call goes
                into Work next. Pin and Unpin are what the button does.
              */
              variant="primary"
              disabled={act.isPending}
              onClick={() => act.mutate(() => salesApi.pin(id, !row.pinnedAt))}
              title="Move this to the front of everybody's worklist"
            >
              {row.pinnedAt ? "Unpin" : "Pin"}
            </Button>
          </span>
        ) : null}
      </section>

      {row.details ? (
        <section className="mb-4 rounded-lg border border-line bg-surface p-3">
          <h2 className="mb-1 text-[11px] font-medium uppercase tracking-wide text-faint">
            Notes
          </h2>
          <p className="whitespace-pre-wrap text-sm text-content">{row.details}</p>
        </section>
      ) : null}

      {/*
        Outside the open branch below, because "Close + email" opens it once
        the close is in -- and by the time the refetch lands the call is
        closed, so a dialog drawn only for an open call vanished as it
        appeared.
      */}
      {emailing ? (
        <NewTicketDialog
          initial={{
            to: row.contactEmail
              ? [{ email: row.contactEmail, name: row.contactName ?? undefined }]
              : [],
            subject: "Following up on our call",
          }}
          onClose={() => setEmailing(false)}
        />
      ) : null}

      {row.status === "open" ? (
        <>
          {/*
            Logging is a dialog, not a panel.

            It was inline at the top of the page and that was wrong twice over:
            it pushed the thing you came to read -- what has already been tried
            -- below the fold, and it sat there open when nobody was ringing
            anybody. A form you summon and dismiss matches what the moment
            actually is.
          */}
          {logging ? (
            <AttemptDialog
              pending={act.isPending}
              onClose={() => setLogging(false)}
              onLog={(input) => {
                act.mutate(() => salesApi.logAttempt(id, input));
                setLogging(false);
              }}
            />
          ) : null}

          {/*
            Booking straight off the call.

            "Rang them, they want to meet" is the most common way a meeting
            gets into the diary at all, and making somebody close this, find
            the diary and retype the company is how it ends up in their own
            calendar instead. The new meeting carries the call's id, so the two
            records point at each other afterwards, and booking one drops you
            into the Meetings product with it open.
          */}
          {booking ? (
            <NewMeetingDialog
              onClose={() => setBooking(false)}
              currentUserId={user.id}
              company={{ id: row.companyId, name: row.companyName }}
              call={{ id: row.id, contactId: row.contactId }}
            />
          ) : null}

          {/* Two rows on a phone: the attempt across the width, as the thing
              this page is opened to do, and the other two halving the line
              under it -- rather than the three wrapping into a staircase. */}
          <div className="mb-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
            <Button className="col-span-2" onClick={() => setLogging(true)}>
              Log an attempt
            </Button>
            <Button variant="secondary" onClick={() => setBooking(true)}>
              Book a meeting
            </Button>
            {/*
              Beside the other two, because closing is the third thing somebody
              does on this page and it was the only one that did not look like
              a control. The form itself was a collapsed disclosure under these
              buttons -- a grey row with a triangle, reading as a heading of
              whatever came next -- so a rep who had made the call and wanted
              to write it up found "Log an attempt", "Book a meeting" and, to
              all appearances, no way to close anything.
            */}
            <Button variant="secondary" onClick={() => setClosing(!closing)}>
              {closing ? "Not now" : "Close the call"}
            </Button>
          </div>

          <CloseForm
            open={closing}
            scores={scores}
            attempts={row.attempts}
            pending={act.isPending}
            onClose={(input, next) =>
              act.mutate(
                async () => {
                  await salesApi.close(id, input);
                  if (next === "call") {
                    await salesApi.followUp(id, { note: input.closingNotes });
                  }
                },
                /* Only once the close is in: an email raised against a close
                   that failed would be the one record of the call. */
                { onSuccess: () => setEmailing(next === "email") },
              )
            }
          />
        </>
      ) : (
        <section className="mb-4 rounded-lg border border-line bg-surface p-3">
          <h2 className="mb-1 text-sm font-medium text-content">
            Closed by {row.closedByName ?? "somebody"}
            {row.closedAt ? ` ${shortDateTime(row.closedAt)}` : ""}
          </h2>
          {row.closingNotes ? (
            <p className="whitespace-pre-wrap text-sm text-muted">
              {row.closingNotes}
            </p>
          ) : null}
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={act.isPending}
              onClick={() => act.mutate(() => salesApi.reopen(id))}
            >
              Reopen
            </Button>
            {/*
              A second round of chasing is a new call, not a reopen. Reopening
              says "we closed this too soon"; this says "we are going back to
              them", and keeping them apart is what stops the first pursuit's
              history being overwritten by the second.
            */}
            <Link to="/sales/calls">
              <Button size="sm" variant="ghost">
                Back to the list
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* ── What has been tried ────────────────────────────────────────── */}
      <section className="mb-4">
        <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-faint">
          {row.attempts.length === 0
            ? "No attempts yet"
            : `${row.attempts.length} attempt${row.attempts.length === 1 ? "" : "s"}`}
        </h2>

        {row.attempts.length === 0 ? (
          <p className="rounded-lg border border-dashed border-line p-4 text-center text-xs text-faint">
            Nobody has rung them. That is why this is at the top of the list.
          </p>
        ) : (
          <ol className="overflow-hidden rounded-lg border border-line bg-surface">
            {row.attempts.map((attempt) => (
              <li
                key={attempt.id}
                className="flex gap-3 border-b border-line px-3 py-2 last:border-b-0"
              >
                <Avatar
                  agent={{ id: attempt.userId ?? 0, name: attempt.userName }}
                  size="xs"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 text-xs">
                    <span className="font-medium text-content">
                      {ATTEMPT_LABELS[attempt.outcome]}
                    </span>
                    <span className="text-faint">
                      {attempt.userName ?? "Somebody"},{" "}
                      <time
                        dateTime={attempt.occurredAt}
                        title={absoluteTime(attempt.occurredAt)}
                      >
                        {shortDateTime(attempt.occurredAt)}
                      </time>
                    </span>
                    {attempt.callbackAt ? (
                      <span className="text-muted">
                        ring back {shortDateTime(attempt.callbackAt)}
                      </span>
                    ) : null}
                  </div>
                  {attempt.note ? (
                    <p className="mt-0.5 whitespace-pre-wrap text-sm text-muted">
                      {attempt.note}
                    </p>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* ── The audit trail ────────────────────────────────────────────── */}
      <details className="mb-6 rounded-lg border border-line bg-surface">
        <summary className="cursor-pointer px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-faint hover:bg-surface-2">
          History
        </summary>
        <ol className="border-t border-line">
          {row.events.map((event) => (
            <li
              key={event.id}
              className="flex flex-wrap items-baseline gap-x-2 border-b border-line px-3 py-1.5 text-xs last:border-b-0"
            >
              <span className="text-content">{describeEvent(event.type)}</span>
              <span className="text-faint">
                {event.actorName ?? "Somebody"},{" "}
                <span title={absoluteTime(event.createdAt)}>
                  {shortDateTime(event.createdAt)}
                </span>
              </span>
            </li>
          ))}
        </ol>
      </details>

      {/*
        Deleting is a manager's, and the API refuses once anybody has actually
        rung -- work that happened is closed with a score, never erased. So the
        button only appears where it can succeed.
      */}
      {/*
        Never once it is closed, even with no attempts against it. A closed
        call is the record that somebody decided about this account -- the
        score and the closing note are the evidence -- and "raised by mistake"
        stops being true the moment somebody sat down and closed it off.
      */}
      {isAdmin && row.attemptCount === 0 && row.status !== "closed" ? (
        <div className="mb-8">
          <Button
            size="sm"
            variant="danger"
            disabled={act.isPending}
            onClick={() =>
              act.mutate(async () => {
                await salesApi.remove(id);
                onClose();
              })
            }
          >
            Delete this call
          </Button>
          <p className="mt-1 text-[11px] text-faint">
            For calls that should never have been raised. Once anybody has rung,
            close it with a score instead.
          </p>
        </div>
      ) : null}
    </Overlay>
  );
}

/**
 * Log a try.
 *
 * A modal, dismissed by the cross, by Escape, or by clicking away -- three
 * ways out because this opens mid-call and the one thing worse than a fiddly
 * form is a fiddly form you cannot get rid of.
 *
 * The outcome is a row of buttons rather than a dropdown, because this is
 * pressed with a phone in one hand and because three of the five are what
 * actually happens, so they should be one press each. The note is optional and
 * the callback date only appears when it can mean anything.
 *
 * The follow-ups -- an email, the next call -- are on the close form, not
 * here. They were here first, and the desk found itself doing everything
 * twice: a call that ends in "I'll send that over" is a call that is finished,
 * so it was logged here with a follow-up and then closed there anyway.
 */
function AttemptDialog({
  pending,
  onClose,
  onLog,
}: {
  pending: boolean;
  onClose: () => void;
  onLog: (input: {
    outcome: SalesAttemptOutcome;
    note?: string | null;
    callbackAt?: string | null;
  }) => void;
}) {
  const [outcome, setOutcome] = useState<SalesAttemptOutcome>("no_answer");
  const [note, setNote] = useState("");
  const [callbackAt, setCallbackAt] = useState("");

  const log = (): void =>
    onLog({
      outcome,
      note: note.trim() || null,
      callbackAt:
        outcome === "callback_requested" && callbackAt
          ? new Date(callbackAt).toISOString()
          : null,
    });

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8"
      /* Clicking the backdrop closes. Guarded on the target being the backdrop
         itself, or a click that started inside the card and drifted out while
         selecting text would dismiss the form and lose what was typed. */
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-lg border border-line bg-surface shadow-xl">
        <header className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold text-content">Log an attempt</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-faint hover:text-brand-600 dark:hover:text-brand-400"
          >
            ✕
          </button>
        </header>

        <div className="space-y-3 p-4">
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(ATTEMPT_LABELS) as SalesAttemptOutcome[]).map((value) => (
              <Chip
                key={value}
                on={outcome === value}
                onClick={() => setOutcome(value)}
              >
                {ATTEMPT_LABELS[value]}
              </Chip>
            ))}
          </div>

          {outcome === "callback_requested" ? (
            <label className="flex flex-wrap items-center gap-2 text-xs text-muted">
              Ring back at
              <input
                type="datetime-local"
                value={callbackAt}
                onChange={(event) => setCallbackAt(event.target.value)}
                className="h-7 rounded-md border border-line bg-surface px-1.5 text-xs text-content"
              />
              {/* The rule this suppresses, said plainly, so nobody wonders why
                  the red badge went away. */}
              <span className="text-faint">stops it being chased until then</span>
            </label>
          ) : null}

          <textarea
            autoFocus
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
            placeholder="What they said"
            className="w-full resize-y rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-content placeholder:text-faint focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
          />

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={pending} onClick={log}>
              Log it
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Whether the close form should arrive with "I spoke to them" already ticked.
 *
 * Ticked is right for the usual close: somebody rings a lead, gets a decision
 * either way, and closes the call there and then. That close is a conversation
 * and the day's figures should say so -- and a box you have to notice and tick
 * to make them say so is a box that gets missed, which is the whole of the bug
 * this exists to fix.
 *
 * Untick it when a conversation has already been logged against this call
 * today, which is the other common shape: log the attempt from the form above,
 * then close off what it settled. That is one conversation, and ticking by
 * default would count it twice. An inflated figure is no more use to the desk
 * than an empty one.
 *
 * Only *answered* attempts suppress it. Three no-answers this morning and then
 * finally getting through is still a conversation nobody has recorded.
 */
function spokeByDefault(attempts: SalesAttempt[], now: Date): boolean {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  return !attempts.some(
    (attempt) =>
      ANSWERED_OUTCOMES.includes(attempt.outcome) &&
      new Date(attempt.occurredAt) >= startOfToday,
  );
}

/**
 * Close it off.
 *
 * Scores are buttons in a row, not a dropdown. Closing a call is the last
 * thirty seconds of a conversation and it competes with simply not bothering:
 * a dropdown is open-read-pick-confirm, four interactions to record one fact,
 * and every one of them is a chance to do it later and never do it. One press
 * and a Close button is the whole thing.
 *
 * Still shut rather than always open, because a score lands in the customer's
 * CRM history permanently and the last thing this form should be is something
 * you can hit by accident while scrolling. What it is no longer is *hidden*:
 * it used to be a `<details>` whose summary was the only way in, and a
 * disclosure triangle among filled buttons does not read as an action -- which
 * is how somebody who had just made the call could not find a way to write it
 * up. It is opened from the button beside the other two now.
 *
 * Two of its buttons do the next thing as well, because a call so often ends
 * with one: "Close + email" closes it and opens a new ticket to the contact,
 * for the "I'll send that over" a rep says before hanging up, and "Close +
 * follow-up call" raises the next call in Medium priority with the closing
 * notes on it -- see `follow-up.ts` on the server. Both were a close, a trip
 * to another product and the company retyped.
 */
function CloseForm({
  open,
  scores,
  attempts,
  pending,
  onClose,
}: {
  open: boolean;
  scores: { id: number; label: string; tone: string }[];
  attempts: SalesAttempt[];
  pending: boolean;
  onClose: (
    input: {
      scoreId: number;
      closingNotes: string | null;
      spoke: boolean;
    },
    next: "email" | "call" | null,
  ) => void;
}) {
  const [scoreId, setScoreId] = useState<number | null>(null);
  const [notes, setNotes] = useState("");

  /*
   * Null means "nobody has touched it", so the tick keeps following the call
   * until somebody decides for themselves. That matters because this form
   * stays mounted behind its disclosure: initialising a `useState` from the
   * attempts would freeze the answer at whatever was true when the page loaded,
   * and logging an attempt and then opening this would still arrive ticked --
   * exactly the double count the default is written to avoid.
   */
  const [spokeChoice, setSpokeChoice] = useState<boolean | null>(null);
  const spoke = spokeChoice ?? spokeByDefault(attempts, new Date());

  /** What is still needed, or null when the form is ready to send. */
  const missing =
    scoreId === null && notes.trim() === ""
      ? "Pick how it went and say what happened."
      : scoreId === null
        ? "Pick how it went."
        : notes.trim() === ""
          ? "Say what happened."
          : null;

  if (!open) return null;

  return (
    <section className="mb-4 rounded-lg border border-line bg-surface">
      <h2 className="px-3 py-2 text-sm font-medium text-content">
        Close this call
      </h2>

      <div className="border-t border-line p-3">
        {scores.length === 0 ? (
          <p className="text-xs text-faint">
            No scores are set up. A manager needs to add one in Settings before
            calls can be closed.
          </p>
        ) : (
          <>
            <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-faint">
              How did it go?
            </p>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {scores.map((score) => (
                <Chip
                  key={score.id}
                  on={scoreId === score.id}
                  onClick={() =>
                    setScoreId(scoreId === score.id ? null : score.id)
                  }
                >
                  {score.label}
                </Chip>
              ))}
            </div>

            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={3}
              placeholder="What happened? Required — this goes into the company's CRM history"
              className="mb-2 w-full resize-y rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-content placeholder:text-faint focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
            />

            {/*
              Every "calls today" figure on the board counts logged attempts,
              and closing used to write none -- so a rep who rang somebody, got
              an answer and closed the call watched the total stay at nought.

              Asked rather than assumed, because plenty of closes are
              paperwork: no opportunity, a duplicate, a lead that went quiet in
              March. Counting those as conversations would move the figure the
              other way and lose the board the same trust.
            */}
            <label className="mb-2 flex items-start gap-2 text-xs text-muted">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={spoke}
                onChange={(event) => setSpokeChoice(event.target.checked)}
              />
              <span>
                I spoke to them
                <span className="text-faint">
                  {" "}
                  — logs this as a call in today's figures
                </span>
              </span>
            </label>

            {/*
              The note is as required as the score.

              A score alone says "Not interested" and nothing about why, and
              this is the text that gets written into the company's CRM
              timeline -- so an empty one leaves the next person ringing that
              account with a closed call and no idea what was said.
            */}
            <div className="flex flex-wrap items-center gap-2">
              {(
                [
                  [null, "Close it off"],
                  ["email", "Close + email"],
                  ["call", "Close + follow-up call"],
                ] as const
              ).map(([next, label]) => (
                <Button
                  key={label}
                  size="sm"
                  variant={next === null ? "primary" : "secondary"}
                  disabled={missing !== null || pending}
                  onClick={() =>
                    scoreId !== null &&
                    notes.trim() !== "" &&
                    onClose({ scoreId, closingNotes: notes.trim(), spoke }, next)
                  }
                >
                  {label}
                </Button>
              ))}

              {/*
                What it is waiting for, beside the button rather than nowhere.
                Both requirements are argued for above and neither is going
                away -- but a greyed-out button that does not say which of the
                two is missing is indistinguishable from one that is broken,
                and gets reported as "I cannot close the call".
              */}
              {missing !== null ? (
                <span className="text-[11px] text-faint">{missing}</span>
              ) : null}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * A date somebody puts on one call.
 *
 * Optional, and it beats every configured rule -- a rep told "the tender
 * closes on the 14th" knows something the group's thresholds do not. Rendered
 * as a plain date input rather than hidden behind an edit mode: it is one
 * field, and a field you have to unlock to change is a field nobody changes.
 */
function DueDate({
  value,
  disabled,
  onSave,
}: {
  value: string | null;
  disabled: boolean;
  onSave: (value: string | null) => void;
}) {
  /* Date only, not date-and-time. Nobody says "due at 14:30" about a sales
     call, and the extra field would be four more keystrokes every time. */
  const asInput = value ? new Date(value).toISOString().slice(0, 10) : "";

  return (
    <label className="flex items-center gap-1.5">
      due
      <input
        type="date"
        value={asInput}
        disabled={disabled}
        onChange={(event) =>
          onSave(
            event.target.value
              ? new Date(`${event.target.value}T23:59:59`).toISOString()
              : null,
          )
        }
        aria-label="Due date"
        className="h-6 rounded-md border border-line bg-surface px-1 text-[11px] text-content"
      />
    </label>
  );
}

/** Event types as a sentence. Unknown types render as themselves, not blank. */
function describeEvent(type: string): string {
  const words: Record<string, string> = {
    created: "Raised",
    claimed: "Claimed",
    released: "Put back in the pool",
    assigned: "Reassigned",
    attempt_logged: "Attempt logged",
    prioritised: "Moved to work next",
    deprioritised: "Unpinned",
    closed: "Closed",
    reopened: "Reopened",
    edited: "Edited",
    deleted: "Deleted",
  };
  return words[type] ?? type.replaceAll("_", " ");
}
